/**
 * `<predictive-search>` custom element (FR13).
 *
 * Fetches the standalone `predictive-search` section as the customer types and
 * renders the suggestions, with keyboard navigation (arrows to move, Enter to
 * open, Escape to close) announced to screen readers.
 *
 * Each keystroke aborts the request in flight: a slow answer for an older
 * term must never paint over a newer one (review of 22/08, #23). Every
 * rendered answer emits `shopify:search:update` (AD-29), zero results included.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.timeout = null;
    this.controller = null;
  }

  connectedCallback() {
    this.input.addEventListener('input', this.onInput.bind(this));
    // Sul componente, non sull'input: dopo la prima freccia il focus sta sui
    // link dei risultati, e i tasti devono continuare a funzionare da lì.
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));

    /* "All categories" (v1.2): scegliere una categoria NAVIGA subito ai
       risultati di quel product_type (con il testo digitato, se c'è); sul
       submit la query si delimita allo stesso modo. Sintassi nativa
       product_type:"..." — senza JS il select è inerte e la ricerca
       semplice funziona comunque. */
    const form = this.querySelector('form');
    const category = this.querySelector('[data-search-category]');
    if (form && category) {
      const go = (type) => {
        const term = this.input.value.trim();
        const q = term ? `${term} product_type:"${type}"` : `product_type:"${type}"`;
        window.location.href = `${form.action}?q=${encodeURIComponent(q)}&type=product`;
      };
      category.addEventListener('change', () => {
        if (category.value) go(category.value);
      });
      form.addEventListener('submit', (event) => {
        if (!category.value) return;
        event.preventDefault();
        go(category.value);
      });
    }
  }

  onInput() {
    clearTimeout(this.timeout);
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.clear();
      return;
    }
    this.timeout = setTimeout(() => this.fetchResults(term), 300);
  }

  fetchResults(term) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const base = this.dataset.url || '/search/suggest';
    const url = `${base}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,article,page&resources[limit]=4`;
    fetch(url, { signal: this.controller.signal })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const updated = doc.querySelector('[data-predictive-search-results]');
        if (!updated) return;
        this.results.innerHTML = updated.innerHTML;
        window.THCabina.events.dispatch('shopify:search:update', {
          terms: updated.dataset.terms || term,
          resultsCount: parseInt(updated.dataset.resultsCount, 10) || 0
        });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.warn('[thcabina] predictive search failed:', error.message);
      });
  }

  onKeydown(event) {
    const items = Array.from(this.results.querySelectorAll('a'));
    if (items.length === 0) return;

    let index = items.indexOf(document.activeElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = items[(index + 1) % items.length];
      next.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = items[(index - 1 + items.length) % items.length];
      next.focus();
    } else if (event.key === 'Escape') {
      this.clear();
      this.input.focus();
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.clear();
    }
  }

  clear() {
    this.results.innerHTML = '';
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
